/**
 * La **pile de fenêtres** du bureau : quelles fenêtres sont ouvertes, dans quel ordre, et laquelle
 * est devant.
 *
 * Le bureau n'affichait **qu'une** fenêtre jusqu'au 7 septembre 2026. C'était une décision
 * d'accélération, et l'enseignant l'a renversée en une phrase : « ce que je comprends pas c'est
 * pourquoi je peux pas avoir plusieurs applications qui s'ouvrent en même temps ». Ce fichier est
 * la réponse.
 *
 * **L'adresse désigne la fenêtre du premier plan.** C'est le seul point de vérité pour le focus :
 * il n'existe pas de `focusedKey` en état React qui pourrait mentir. Recharger la page rouvre donc
 * la fenêtre qui était devant (les autres, elles, ne survivent pas au rechargement — une pile de
 * fenêtres n'est pas une donnée, c'est une session).
 *
 * Ce qui vit ici, en revanche, est ce que l'adresse ne peut pas porter : **quelles autres fenêtres
 * sont ouvertes**, leur ordre d'empilement (`z`), leur état réduit, et l'adresse interne que
 * chacune retrouvera quand elle repassera devant.
 *
 * Toutes les fonctions sont pures et rendent le **même objet** quand rien ne change : l'effet de
 * synchronisation du bureau (`Desk.tsx`) les rejoue à chaque changement d'adresse, et une nouvelle
 * référence rendue pour rien relancerait un rendu à chaque fois.
 */

export type WindowKind = 'app' | 'me' | 'teacher' | 'activity' | 'documents';

export interface DeskWindow {
  /** Identité stable de la fenêtre : `app:<id>`, `me`, `teacher`, `activity`, `documents`. Une app ouverte
   * deux fois est la même fenêtre — c'est ce qui empêche le doublon quand on reclique son icône du
   * dock. */
  readonly key: string;
  readonly kind: WindowKind;
  /** L'id de l'app pour `kind: 'app'`, sinon `null`. Le cœur ne le lit que pour le dock et la
   * présence ; il ne le compare jamais à une valeur écrite en dur. */
  readonly appId: string | null;
  /** L'adresse **complète** de la fenêtre, sous-route de l'app comprise (`/app/crm/contacts`).
   * C'est ce qu'on rejoue quand la fenêtre repasse devant. */
  readonly path: string;
  readonly minimized: boolean;
  /** Rang d'empilement : plus grand = plus près de l'observateur. Strictement croissant. */
  readonly z: number;
  /**
   * **La place de la fenêtre dans la cascade** — 0 pour la première ouverte, 1 pour la suivante,
   * etc. `AppWindow` en tire un décalage initial (`useWindowDrag`), borné pour ne jamais sortir de
   * l'écran ni passer sous le dock.
   *
   * Sans ce champ, toutes les fenêtres s'ouvraient exactement aux mêmes coordonnées (160, 64 —
   * mesuré le 7 septembre 2026) : ouvrir une seconde app MASQUAIT la première au lieu d'en donner
   * deux. C'est le comportement que l'enseignant avait explicitement demandé — « pourquoi je peux
   * pas avoir plusieurs applications qui s'ouvrent en même temps » — et qui restait invisible.
   *
   * La place attribuée est la **plus petite libre**, jamais un compteur qui monte : fermer la
   * fenêtre du milieu puis en ouvrir une autre reprend le trou plutôt que de repousser la cascade
   * toujours plus loin, et deux fenêtres ne peuvent pas partager une place.
   */
  readonly cascade: number;
}

export interface WindowStack {
  readonly windows: readonly DeskWindow[];
  /** Prochain rang à distribuer. Ne décroît jamais ; les rangs réels sont ensuite remis à plat au
   * rendu (`orderedWindows`), donc ce compteur ne fait grossir aucun `z-index`. */
  readonly nextZ: number;
}

export const EMPTY_STACK: WindowStack = { windows: [], nextZ: 1 };

/** Le bureau nu : aucune fenêtre au premier plan. */
export const DESK_PATH = '/';

/**
 * L'identité de fenêtre que désigne une adresse — `null` si l'adresse n'ouvre aucune fenêtre (le
 * bureau nu, ou une adresse qui ne correspond à rien).
 */
export function windowKeyOf(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const [head, second] = segments;
  if (head === 'app') return second === undefined ? null : `app:${second}`;
  if (head === 'me') return 'me';
  if (head === 'teacher') return 'teacher';
  if (head === 'activity') return 'activity';
  if (head === 'documents') return 'documents';
  return null;
}

/**
 * L'adresse **rangée** d'une fenêtre : celle qu'on mémorise et qu'on rejouera.
 *
 * Une seule normalisation, et elle a une raison précise : `/teacher` tout court ne correspond à
 * aucune des routes de l'espace enseignant (`/teacher/:section`), la fenêtre s'ouvrirait donc
 * vide. On la range à `/teacher/presence` — l'écran de la séance en cours, celui qu'on ouvre le
 * plus souvent. L'adresse de la barre du navigateur, elle, n'est pas réécrite : la réécrire
 * n'apporterait rien et ajouterait une navigation.
 */
export function normalizePath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const [head, second] = segments;
  if (head === 'app') return second === undefined ? null : pathname;
  if (head === 'me') return '/me';
  if (head === 'activity') return '/activity';
  if (head === 'documents') return '/documents';
  if (head === 'teacher') return second === undefined ? '/teacher/presence' : pathname;
  return null;
}

function describe(key: string, path: string): { kind: WindowKind; appId: string | null } {
  if (key.startsWith('app:')) return { kind: 'app', appId: key.slice('app:'.length) };
  if (path.startsWith('/teacher')) return { kind: 'teacher', appId: null };
  if (path.startsWith('/activity')) return { kind: 'activity', appId: null };
  if (path.startsWith('/documents')) return { kind: 'documents', appId: null };
  return { kind: 'me', appId: null };
}

/** Les fenêtres du fond vers le premier plan. C'est l'ordre de rendu et l'ordre des `z-index`. */
export function orderedWindows(stack: WindowStack): readonly DeskWindow[] {
  return [...stack.windows].sort((a, b) => a.z - b.z);
}

/** La fenêtre qui doit prendre le premier plan quand celle de devant s'en va : la plus haute qui
 * soit encore visible. Aucune : le bureau nu. */
export function nextFocusPath(stack: WindowStack): string {
  const visible = orderedWindows(stack).filter((w) => !w.minimized);
  return visible[visible.length - 1]?.path ?? DESK_PATH;
}

/**
 * Met au premier plan la fenêtre que désigne l'adresse — en l'ouvrant si elle ne l'était pas, en
 * la faisant revenir si elle était réduite, et en mémorisant sa sous-route.
 *
 * C'est la **seule** fonction qui ouvre une fenêtre : ouvrir, cliquer une icône du dock déjà
 * lancée, revenir en arrière dans l'historique et recharger la page passent tous par une
 * navigation, donc tous par ici. D'où l'absence de doublon, sans aucune garde ailleurs.
 */
export function focusOrOpen(stack: WindowStack, pathname: string): WindowStack {
  const path = normalizePath(pathname);
  if (path === null) return stack;
  const key = windowKeyOf(path);
  if (key === null) return stack;

  const existing = stack.windows.find((w) => w.key === key);
  if (existing !== undefined) {
    const alreadyFront = stack.windows.every((w) => w.z <= existing.z);
    if (alreadyFront && !existing.minimized && existing.path === path) return stack;
    return {
      windows: stack.windows.map((w) =>
        w.key === key ? { ...w, path, minimized: false, z: stack.nextZ } : w,
      ),
      nextZ: stack.nextZ + 1,
    };
  }

  return {
    windows: [
      ...stack.windows,
      {
        key,
        ...describe(key, path),
        path,
        minimized: false,
        z: stack.nextZ,
        cascade: freeCascade(stack.windows),
      },
    ],
    nextZ: stack.nextZ + 1,
  };
}

/** La plus petite place de cascade qu'aucune fenêtre ouverte n'occupe. Pure et déterministe : la
 * même pile rend toujours la même place. */
function freeCascade(windows: readonly DeskWindow[]): number {
  const taken = new Set(windows.map((w) => w.cascade));
  let slot = 0;
  while (taken.has(slot)) slot += 1;
  return slot;
}

/** Ferme une fenêtre. Son contenu est démonté : c'est ce que « fermer » veut dire. */
export function closeWindow(stack: WindowStack, key: string): WindowStack {
  const windows = stack.windows.filter((w) => w.key !== key);
  return windows.length === stack.windows.length ? stack : { ...stack, windows };
}

/** Réduit une fenêtre. Elle reste **montée** : l'app garde son état et son point reste allumé dans
 * le dock. La démonter ferait perdre le travail en cours. */
export function minimizeWindow(stack: WindowStack, key: string): WindowStack {
  const target = stack.windows.find((w) => w.key === key);
  if (target === undefined || target.minimized) return stack;
  return {
    ...stack,
    windows: stack.windows.map((w) => (w.key === key ? { ...w, minimized: true } : w)),
  };
}

/** Stockage non fiable : reconstruire la pile par les mêmes règles que la navigation. */
export function restoreStack(raw: unknown): WindowStack {
  if (!raw || typeof raw !== 'object' || !('windows' in raw) || !Array.isArray(raw.windows))
    return EMPTY_STACK;
  let restored = EMPTY_STACK;
  for (const value of raw.windows.slice(0, 32) as unknown[]) {
    if (!value || typeof value !== 'object' || !('path' in value) || typeof value.path !== 'string')
      continue;
    if (!value.path.startsWith('/') || value.path.startsWith('//')) continue;
    restored = focusOrOpen(restored, value.path);
    if ('minimized' in value && value.minimized === true) {
      const key = windowKeyOf(value.path);
      if (key !== null) restored = minimizeWindow(restored, key);
    }
  }
  return restored;
}
